import { ApiError, consoleBffRequestUrl, getConsoleBffCsrfToken } from './client';

export interface SetupTotpChallenge { challengeId: string; expiresAt: string; attempt: number }
export type SetupInputOperation = 'catalog' | 'verify_environment' | 'start_job';

/** Short-lived socket to the same API owner. Codes never enter storage or URLs. */
export class SetupInputConnection {
  private readonly socket: WebSocket;
  private readonly ready: Promise<void>;
  private pending?: { id: string; resolve: (value: unknown) => void; reject: (error: Error) => void };
  private closed = false;
  private challenge?: SetupTotpChallenge;
  constructor(private readonly onChallenge: (value: SetupTotpChallenge | null) => void,
    private readonly onError: (message: string) => void) {
    const url = new URL(consoleBffRequestUrl('/console-bff/setup-input'), location.href);
    // consoleBffRequestUrl resolves the configured API origin, including the
    // separate API host used by the public Console and E2E tunnels.
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    this.socket = new WebSocket(url);
    this.ready = new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => { reject(Error('入力接続を開けませんでした。')); this.close(); }, 10_000);
      this.socket.addEventListener('message', event => {
        try {
          const value = JSON.parse(String(event.data));
          if (value.kind === 'ready') { clearTimeout(timer); resolve(); return; }
          if (value.kind === 'challenge') {
            if (typeof value.challengeId !== 'string' || typeof value.expiresAt !== 'string' || !Number.isFinite(Date.parse(value.expiresAt)) || ![1,2,3].includes(value.attempt)) throw Error();
            this.challenge = { challengeId: value.challengeId, expiresAt: value.expiresAt, attempt: value.attempt };
            this.onChallenge(this.challenge); return;
          }
          if (value.kind === 'code_received') { if (this.challenge?.challengeId === value.challengeId) { this.challenge = undefined; this.onChallenge(null); } return; }
          if (value.kind === 'input_error') { this.onError('認証コードを確認し、現在の入力要求へ送信してください。'); return; }
          if ((value.kind === 'response' || value.kind === 'request_error') && this.pending?.id === value.requestId) {
            const pending = this.pending!; this.pending = undefined;
            if (value.kind === 'request_error') { pending.reject(Error('同じ入力接続で別の処理が実行中です。')); return; }
            if (value.status < 200 || value.status >= 300) {
              const error = value.body;
              pending.reject(new ApiError(value.status, error?.code ?? 'setup_input_failed', error?.message ?? '設定を続行できませんでした。'));
            } else pending.resolve(value.body);
          }
        } catch { this.close(); }
      });
      const lost = () => { clearTimeout(timer); reject(Error('多要素認証の入力接続が終了しました。')); this.fail(); };
      this.socket.addEventListener('close', lost);
      this.socket.addEventListener('error', lost);
    });
    // A lost idle connection is reported through the callback, never as an unhandled rejection.
    void this.ready.catch(() => undefined);
  }
  async request<T>(operation: SetupInputOperation, body: unknown): Promise<T> {
    await this.ready;
    if (this.closed || this.pending) throw Error('多要素認証の入力接続を使用できません。');
    const csrfToken = await getConsoleBffCsrfToken();
    if (this.closed || this.pending) throw Error('多要素認証の入力接続を使用できません。');
    const id = crypto.randomUUID();
    return new Promise<T>((resolve, reject) => {
      this.pending = { id, resolve: value => resolve(value as T), reject };
      try { this.socket.send(JSON.stringify({ kind: 'request', requestId: id, operation, csrfToken, body })); }
      catch { this.fail(); }
    });
  }
  async submit(code: string): Promise<void> {
    if (this.closed || !this.challenge || !/^[0-9]{6}$/.test(code) || Date.parse(this.challenge.expiresAt) <= Date.now()) throw Error('認証コードの入力要求が無効です。');
    const challengeId = this.challenge.challengeId;
    const csrfToken = await getConsoleBffCsrfToken();
    if (this.closed || this.challenge?.challengeId !== challengeId) throw Error('認証コードの入力要求が終了しました。');
    try { this.socket.send(JSON.stringify({ kind: 'code', challengeId, code, csrfToken })); }
    finally { code = ''; }
  }
  close(): void { this.fail(); this.socket.close(); }
  private fail(): void {
    if (this.closed) return;
    this.closed = true; this.challenge = undefined; this.onChallenge(null);
    const pending = this.pending; this.pending = undefined;
    pending?.reject(new ApiError(0, 'network_error', '多要素認証の入力接続が終了しました。自動再実行はしません。'));
    this.onError('多要素認証の入力接続が終了しました。');
  }
}
